import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import MushafPageView from "@/components/MushafPageView";
import AyahActionMenu from "@/components/AyahActionMenu";
import TranslationDialog from "@/components/TranslationDialog";
import PageSelectionToolbar from "@/components/PageSelectionToolbar";
import RangeArmedHint from "@/components/RangeArmedHint";
import { computeSelectedKeys } from "@/lib/pageSelection";
import { toast } from "sonner";
import { API, useAuth, authFetch } from "@/App";
import { SURAH_LIST, getJuzForSurah } from "@/data/quranMeta";
import { removeBismillahPrefix } from "@/lib/quranText";
import { RECITER_FOLDERS } from "@/data/reciters";
import ReaderTutorial, { shouldShowTutorial } from "@/components/ReaderTutorial";
import ViewModePicker, {
  shouldShowViewModePicker,
  getDefaultViewMode,
} from "@/components/ViewModePicker";
import SurahSelector from "@/components/SurahSelector";
import FlashcardView from "@/components/FlashcardView";
import MushafView from "@/components/MushafView";
import AudioControls from "@/components/AudioControls";
import ReaderHeader from "@/components/ReaderHeader";
import RotateHint from "@/components/RotateHint";
import FocusSessionChip from "@/components/FocusSessionChip";
import useReaderAudio from "@/hooks/useReaderAudio";
import useAyahLongPress from "@/hooks/useAyahLongPress";
import useWakeLock from "@/hooks/useWakeLock";
import { Minimize2 } from "lucide-react";

// Compute the inclusive set of "{surah}:{ayah}" keys covered by a Page-Mode
// range selection. Moved to `/app/frontend/src/lib/pageSelection.js` so it
// can be shared with `PageSelectionToolbar`.

export default function Reader() {
  const { surahNumber } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const oneshotAudioRef = useRef(null);

  // View mode from URL or default (removed page mode)
  const initialViewMode = searchParams.get('mode') || 'mushaf';

  const [surah, setSurah] = useState(null);
  const [translation, setTranslation] = useState(null);
  const [transliteration, setTransliteration] = useState(null);
  const [progress, setProgress] = useState({});
  // Bookmarks lookup — Map of "surah:ayah" → bookmark_id so the AyahActionMenu
  // can render Bookmark vs "Remove bookmark" and toggle in one call.
  const [bookmarkMap, setBookmarkMap] = useState({});
  const [showTranslation, setShowTranslation] = useState(true);
  const [showTransliteration, setShowTransliteration] = useState(false);
  const [showWordByWord, setShowWordByWord] = useState(false);
  // Reading width preset (Surah & Page mode line-height + container width).
  const [readingWidth, setReadingWidth] = useState(() => {
    if (typeof window === "undefined") return "comfortable";
    return localStorage.getItem("quran_reading_width") || "comfortable";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_reading_width", readingWidth);
    }
  }, [readingWidth]);
  const READING_WIDTH_PRESETS = {
    compact: { lineHeight: 2.6, maxWidth: "max-w-3xl", pageFontScale: 0.95 },
    comfortable: { lineHeight: 3.6, maxWidth: "max-w-4xl", pageFontScale: 1.0 },
    wide: { lineHeight: 4.6, maxWidth: "max-w-5xl", pageFontScale: 1.08 },
  };
  const readingPreset = READING_WIDTH_PRESETS[readingWidth] || READING_WIDTH_PRESETS.comfortable;

  // Font size preset for Arabic text (Surah / Flashcard / Page mode).
  const [fontSize, setFontSize] = useState(() => {
    if (typeof window === "undefined") return "medium";
    return localStorage.getItem("quran_font_size") || "medium";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_font_size", fontSize);
    }
  }, [fontSize]);
  const FONT_SIZE_PRESETS = {
    small:   { surahPx: 22, flashPx: 36, pageScale: 0.85 },
    medium:  { surahPx: 30, flashPx: 48, pageScale: 1.0  },
    large:   { surahPx: 38, flashPx: 60, pageScale: 1.18 },
    xlarge:  { surahPx: 46, flashPx: 72, pageScale: 1.35 },
  };
  const fontPreset = FONT_SIZE_PRESETS[fontSize] || FONT_SIZE_PRESETS.medium;
  // Cache for word-by-word data: { "surah:ayah": [{arabic,translation,transliteration}] }
  const [wbwCache, setWbwCache] = useState({});
  const [wbwLoading, setWbwLoading] = useState(false);
  // Tafsir (Flashcard mode only). Toggle persisted to localStorage; cache keyed by `${edition}|${surah}:${ayah}`.
  const [showTafsir, setShowTafsir] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("quran_show_tafsir") === "1";
  });
  const [tafsirEdition, setTafsirEdition] = useState(() => {
    if (typeof window === "undefined") return "en.maududi";
    return localStorage.getItem("quran_tafsir_edition") || "en.maududi";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_show_tafsir", showTafsir ? "1" : "0");
    }
  }, [showTafsir]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_tafsir_edition", tafsirEdition);
    }
  }, [tafsirEdition]);
  const [tafsirCache, setTafsirCache] = useState({});
  const [tafsirLoading, setTafsirLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    // Priority: ?mode= URL param > saved user preference > default 'mushaf'.
    if (['mushaf', 'flashcard', 'page'].includes(initialViewMode)) {
      return initialViewMode;
    }
    const saved = getDefaultViewMode();
    if (['mushaf', 'flashcard', 'page'].includes(saved)) {
      return saved;
    }
    return 'mushaf';
  });
  const [pageAyahs, setPageAyahs] = useState([]); // ayahs visible on current Mushaf page(s)
  // Multi-ayah range selection in Page Mode.
  // anchor = first tap (start), endpoint = second tap (inclusive end). Null until user selects.
  const [pageSelection, setPageSelection] = useState(null);
  // Imperative handle to MushafPageView so audio can flip to the next page.
  const pageControlsRef = useRef(null);
  // When true, the next batch of pageAyahs that loads should auto-play from
  // the first ayah (used for seamless audio continuation across page turns).
  const pendingPageAdvancePlayRef = useRef(false);
  // Continuous page-turn (Page Mode) — time-based, hands-free reading.
  const [continuousPageTurn, setContinuousPageTurn] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("quran_continuous_page_turn") === "1";
  });
  const [continuousPageSeconds, setContinuousPageSeconds] = useState(() => {
    if (typeof window === "undefined") return 30;
    const v = parseInt(localStorage.getItem("quran_continuous_page_seconds") || "30");
    return Number.isFinite(v) && v >= 5 ? v : 30;
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_continuous_page_turn", continuousPageTurn ? "1" : "0");
    }
  }, [continuousPageTurn]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_continuous_page_seconds", String(continuousPageSeconds));
    }
  }, [continuousPageSeconds]);
  const [reviewMode, setReviewMode] = useState(false);
  const [stackMode, setStackMode] = useState(false);
  const [stackLevel, setStackLevel] = useState(1);
  const [stackCurrentIndex, setStackCurrentIndex] = useState(0);
  const [selectedJuz, setSelectedJuz] = useState(getJuzForSurah(parseInt(surahNumber)));
  const [draggedAyah, setDraggedAyah] = useState(null);
  
  // Surah selector state
  const [showSurahSelector, setShowSurahSelector] = useState(false);
  const [surahSearch, setSurahSearch] = useState("");
  
  // Filter surahs based on search (case insensitive, partial match)
  const filteredSurahs = SURAH_LIST.filter(s => {
    const search = surahSearch.toLowerCase().replace(/[-' ]/g, '');
    const englishName = s.englishName.toLowerCase().replace(/[-' ]/g, '');
    return englishName.includes(search) ||
           s.name.includes(surahSearch) ||
           String(s.number) === surahSearch ||
           String(s.number).startsWith(surahSearch);
  });
  
  // Unified "Recitation Range" — one toggle with full surah:ayah granularity
  // on both ends. Persisted to localStorage so the range survives the cross-
  // surah auto-advance navigation (Reader remounts on every /reader/{N}).
  const [rangeEnabled, setRangeEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("quran_range_enabled") === "1";
  });
  const [fromSurah, setFromSurah] = useState(() => {
    if (typeof window === "undefined") return 1;
    const v = parseInt(localStorage.getItem("quran_range_from_surah") || "1");
    return Number.isFinite(v) ? v : 1;
  });
  const [fromAyah, setFromAyah] = useState(() => {
    if (typeof window === "undefined") return 1;
    const v = parseInt(localStorage.getItem("quran_range_from_ayah") || "1");
    return Number.isFinite(v) ? v : 1;
  });
  const [toSurah, setToSurah] = useState(() => {
    if (typeof window === "undefined") return 1;
    const v = parseInt(localStorage.getItem("quran_range_to_surah") || "1");
    return Number.isFinite(v) ? v : 1;
  });
  const [toAyah, setToAyah] = useState(() => {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem("quran_range_to_ayah");
    if (raw == null || raw === "null" || raw === "") return null;
    const v = parseInt(raw);
    return Number.isFinite(v) ? v : null;
  });
  // Persist whenever any range field changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem("quran_range_enabled", rangeEnabled ? "1" : "0");
    localStorage.setItem("quran_range_from_surah", String(fromSurah));
    localStorage.setItem("quran_range_from_ayah", String(fromAyah));
    localStorage.setItem("quran_range_to_surah", String(toSurah));
    localStorage.setItem("quran_range_to_ayah", toAyah == null ? "null" : String(toAyah));
  }, [rangeEnabled, fromSurah, fromAyah, toSurah, toAyah]);
  // Back-compat alias for call sites still referencing the old cross-surah
  // toggle. True iff the unified range spans more than one surah.
  const surahRangeEnabled = rangeEnabled && toSurah > fromSurah;
  const setSurahRangeEnabled = setRangeEnabled;
  
  // Repetition settings
  const [repeatEachVerse, setRepeatEachVerse] = useState(3); // 1-20 or -1 for infinity
  const [repeatSet, setRepeatSet] = useState(1); // 1-20 or -1 for infinity

  const [settings, setSettings] = useState({
    reciter: "ar.alafasy",
    pauseDuration: 2,
    playbackSpeed: 1,
    translation: "en.sahih"
  });

  // Bookmarks now save instantly without a note prompt — no dialog state
  // needed.

  // Reader tutorial overlay (first-visit walkthrough). Auto-shows on the
  // first time the user opens any Reader page, then never again unless they
  // re-trigger it from the help icon.
  const [showTutorial, setShowTutorial] = useState(false);
  const [fullscreenMode, setFullscreenMode] = useState(false);
  const [focusStartedAt, setFocusStartedAt] = useState(null);

  // Keep the screen awake while the reader is open — common UX expectation
  // for a Quran-reading app where users may study for long stretches.
  useWakeLock(true);

  // Surah-range auto-advance & autoplay-on-load effects are declared LATER in
  // the component, after the `audio` hook destructure (avoids TDZ on isPlaying).
  const prevIsPlayingRef = useRef(false);

  // Track start time when fullscreen turns on; on turn-off, POST the seconds
  // to the backend and toast a friendly summary.
  useEffect(() => {
    if (fullscreenMode) {
      setFocusStartedAt(Date.now());
      return;
    }
    if (focusStartedAt) {
      const seconds = Math.floor((Date.now() - focusStartedAt) / 1000);
      setFocusStartedAt(null);
      // Skip ultra-short sessions (< 30s) — likely accidental.
      if (seconds >= 30) {
        const mins = Math.round(seconds / 60);
        const label = mins >= 1 ? `${mins} min` : `${seconds}s`;
        toast.success(`Beautiful focus — ${label} of pure recitation. ﷽`);
        authFetch(`${API}/focus-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ seconds }),
        }).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreenMode]);

  // Esc key exits fullscreen — easy keyboard recovery.
  useEffect(() => {
    if (!fullscreenMode) return;
    const onKey = (e) => {
      if (e.key === "Escape") setFullscreenMode(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreenMode]);
  // View-mode picker — shown right after the tutorial closes (first time only).
  const [showViewPicker, setShowViewPicker] = useState(false);
  useEffect(() => {
    if (shouldShowTutorial()) {
      // Small delay so the Reader has time to paint before the modal appears.
      const t = setTimeout(() => setShowTutorial(true), 800);
      return () => clearTimeout(t);
    }
    // Tutorial already seen — but if the picker hasn't been shown yet
    // (returning user from before this feature shipped), surface it directly.
    if (shouldShowViewModePicker()) {
      const t = setTimeout(() => setShowViewPicker(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleTutorialClose = () => {
    setShowTutorial(false);
    if (shouldShowViewModePicker()) {
      setTimeout(() => setShowViewPicker(true), 250);
    }
  };
  const [playerVisible, setPlayerVisible] = useState(true);
  const idleTimerRef = useRef(null);
  const showPlayerAndArmIdleTimer = useCallback(() => {
    setPlayerVisible(true);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    // Don't auto-hide while the settings panel is open
    if (showSettings) return;
    idleTimerRef.current = setTimeout(() => setPlayerVisible(false), 3000);
  }, [showSettings]);

  useEffect(() => {
    showPlayerAndArmIdleTimer();
    // On desktop: any mouse/keyboard/scroll re-arms the visibility timer.
    // Touch devices use the explicit tap-to-toggle handler below — auto-show
    // on every touchstart is intentionally disabled there so the user has
    // full control over when the audio bar / header appear.
    const events = ["mousemove", "keydown", "scroll"];
    const handler = () => showPlayerAndArmIdleTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [showPlayerAndArmIdleTimer]);

  // Tap anywhere on the Reader (except interactive elements like buttons,
  // inputs, ayahs, dialogs) toggles distraction-free mode — one tap hides
  // the header + audio bar + hints, another tap brings them back. Works on
  // both touch AND mouse (per user spec).
  const handleReaderTap = (e) => {
    const t = e.target;
    if (!t || typeof t.closest !== "function") return;
    if (
      t.closest(
        'button, a, input, textarea, select, [role="button"], [role="dialog"], [role="menu"], [data-radix-popper-content-wrapper], [data-testid^="surah-ayah-"], [data-testid^="page-ayah-"], [data-testid^="audio-"], [data-testid^="player-"], [data-testid="range-armed-hint"], [data-testid="ayah-action-menu-backdrop"], [data-no-toggle]',
      )
    ) {
      return;
    }
    // Ignore if a range is armed — the tap is a range-completion signal
    // handled elsewhere, not a chrome-toggle.
    if (rangeArmedFromRef.current) return;
    // Ignore if the action menu is open — dismissal is owned by the menu
    // backdrop so we don't want to simultaneously toggle fullscreen.
    if (ayahMenuRef.current) return;
    setFullscreenMode((v) => !v);
  };
  
  // Stack mode refs to avoid stale closures (consumed inside useReaderAudio).
  const stackLevelRef = useRef(stackLevel);
  const stackCurrentIndexRef = useRef(stackCurrentIndex);
  const repeatEachVerseRef = useRef(repeatEachVerse);
  const repeatSetRef = useRef(repeatSet);

  useEffect(() => { stackLevelRef.current = stackLevel; }, [stackLevel]);
  useEffect(() => { stackCurrentIndexRef.current = stackCurrentIndex; }, [stackCurrentIndex]);
  useEffect(() => { repeatEachVerseRef.current = repeatEachVerse; }, [repeatEachVerse]);
  useEffect(() => { repeatSetRef.current = repeatSet; }, [repeatSet]);

  // Swipe handling. Uses refs (not state) so the coords read inside
  // onTouchEnd are always the latest — avoids React 18 batching races on
  // fast taps / synthetic events.
  const touchStartXRef = useRef(null);
  const touchEndXRef = useRef(null);
  const minSwipeDistance = 100;

  const onTouchStart = (e) => {
    if (!e.targetTouches?.[0]) return;
    touchEndXRef.current = null;
    touchStartXRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    if (!e.targetTouches?.[0]) return;
    touchEndXRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    const startX = touchStartXRef.current;
    const endX = touchEndXRef.current;
    if (startX == null || endX == null) return;
    const distance = startX - endX;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (!isLeftSwipe && !isRightSwipe) return;

    // Page Mode: navigate the Mushaf page (prev/next page) rather than surah.
    // Per user spec: swipe RIGHT → bigger page number (forward), swipe LEFT
    // → smaller page number (back).
    if (viewMode === "page" && pageControlsRef.current) {
      if (isRightSwipe) pageControlsRef.current.goNext?.();
      else pageControlsRef.current.goPrev?.();
      return;
    }

    // Surah / Flashcard modes: swipe RIGHT → next surah (towards An-Nas / 114),
    // swipe LEFT → previous surah (towards Al-Fatiha / 1).
    const currentSurah = parseInt(surahNumber);
    if (isRightSwipe && currentSurah < 114) navigate(`/reader/${currentSurah + 1}`);
    else if (isLeftSwipe && currentSurah > 1) navigate(`/reader/${currentSurah - 1}`);
  };

  useEffect(() => {
    fetchSurahData();
    fetchProgress();
    fetchBookmarks();
    fetchSettings();
    setSelectedJuz(getJuzForSurah(parseInt(surahNumber)));
    // fetchers depend only on surahNumber + user settings which we intentionally
    // re-fetch on view-mode change (page mode swaps the audio queue shape).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surahNumber, viewMode]);

  // Re-fetch translation when user changes it
  useEffect(() => {
    if (!surah) return; // skip on initial load
    const translationEdition = settings.translation || "en.sahih";
    fetch(`${API}/quran/surah/${surahNumber}/translation?edition=${translationEdition}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setTranslation(data); })
      .catch(() => {});
    // `surah` is intentionally used as a mount-gate only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.translation, surahNumber]);


  const fetchSurahData = useCallback(async () => {
    setIsLoading(true);
    try {
      const translationEdition = settings.translation || "en.sahih";
      const [surahRes, transRes, translitRes] = await Promise.all([
        fetch(`${API}/quran/surah/${surahNumber}`),
        fetch(`${API}/quran/surah/${surahNumber}/translation?edition=${translationEdition}`),
        fetch(`https://api.alquran.cloud/v1/surah/${surahNumber}/en.transliteration`)
      ]);

      if (surahRes.ok) setSurah(await surahRes.json());
      if (transRes.ok) setTranslation(await transRes.json());
      if (translitRes.ok) {
        const data = await translitRes.json();
        setTransliteration(data.data);
      }
    } catch (error) {
      console.error('Error fetching surah:', error);
      toast.error('Failed to load surah');
    } finally {
      setIsLoading(false);
    }
    // We intentionally exclude `settings.translation` — it has its own
    // dedicated re-fetch effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surahNumber]);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/progress`);
      if (res.ok) {
        const data = await res.json();
        const progressMap = {};
        data.forEach(p => {
          const key = `${p.surah_number}:${p.ayah_number}`;
          progressMap[key] = p.is_memorized;
        });
        setProgress(progressMap);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  }, []);

  const fetchBookmarks = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/bookmarks`);
      if (!res.ok) return;
      const data = await res.json();
      const m = {};
      (data || []).forEach((b) => {
        m[`${b.surah_number}:${b.ayah_number}`] = b.bookmark_id;
      });
      setBookmarkMap(m);
    } catch (err) {
      console.error("fetchBookmarks failed:", err);
    }
  }, []);

  const isBookmarked = (surahNum, ayahNum) =>
    !!bookmarkMap[`${surahNum}:${ayahNum}`];

  const fetchSettings = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/settings`);
      if (res.ok) {
        const data = await res.json();
        if (data.memorization_settings) {
          setSettings({
            reciter: data.memorization_settings.reciter || "ar.alafasy",
            pauseDuration: data.memorization_settings.pause_duration || 2,
            playbackSpeed: data.memorization_settings.playback_speed || 1,
            translation: data.memorization_settings.translation || "en.sahih"
          });
          setRepeatEachVerse(data.memorization_settings.loop_count || 3);
          // Sync server-stored display prefs (overrides localStorage so the
          // user's preferences follow them across devices).
          if (data.memorization_settings.reading_width) {
            setReadingWidth(data.memorization_settings.reading_width);
          }
          if (data.memorization_settings.font_size) {
            setFontSize(data.memorization_settings.font_size);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  // Persist display prefs (reading width + font size) to the user's account
  // whenever they change. Skipped during the initial mount before the first
  // server fetch completes (we only have localStorage values at that point).
  //
  // IMPORTANT: We send ONLY the fields that this effect owns (reading_width,
  // font_size). The backend uses dot-notation `$set` so other sub-fields
  // (reciter, translation, loop_count, …) stay intact. Sending them here
  // would clobber any changes the user made on the /settings page since
  // Reader mounted, because Reader's local `settings` cache is stale.
  const initialPrefsLoadedRef = useRef(false);
  useEffect(() => {
    if (!initialPrefsLoadedRef.current) {
      initialPrefsLoadedRef.current = true;
      return;
    }
    authFetch(`${API}/settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        memorization_settings: {
          reading_width: readingWidth,
          font_size: fontSize,
        },
      }),
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readingWidth, fontSize]);

  // Continuous page-turn: while ON in Page Mode, auto-advance the Mushaf
  // page every `continuousPageSeconds`. Hands-free reading for meditation.
  // Stops automatically when toggled off, mode changes, or last page reached.
  useEffect(() => {
    if (!continuousPageTurn) return;
    if (viewMode !== "page") return;
    const interval = setInterval(() => {
      const c = pageControlsRef.current;
      if (c && typeof c.goNext === "function") {
        if (typeof c.canGoNext !== "function" || c.canGoNext()) {
          c.goNext();
        } else {
          // Reached the last page — auto-disable to stop the loop.
          setContinuousPageTurn(false);
        }
      }
    }, Math.max(5, continuousPageSeconds) * 1000);
    return () => clearInterval(interval);
  }, [continuousPageTurn, continuousPageSeconds, viewMode]);

  // If the Dashboard's "first-time reciter" nudge was clicked, the user
  // landed here with a one-shot localStorage flag asking us to open the
  // Compare Reciters dialog. We expand the audio settings panel so that
  // CompareReciters mounts — its own mount-time effect then reads+clears
  // the flag and flips the dialog open. Also suppress the Reader tutorial
  // for this one-shot flow so it doesn't overlap the Compare dialog.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("quran_open_compare_on_mount") === "1") {
      localStorage.setItem("quran_reader_tutorial_done", "1");
      setShowSettings(true);
    }
  }, []);

  // Page pin — long-press the Mushaf page indicator stores a "Resume here"
  // pin. Fetched once on mount; updated optimistically on save.
  const [pinnedPage, setPinnedPage] = useState(null);
  useEffect(() => {
    authFetch(`${API}/page-pin`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.page_pin?.page) setPinnedPage(data.page_pin.page);
      })
      .catch(() => {});
  }, []);
  const handlePagePin = (page) => {
    if (!page) return;
    setPinnedPage(page);
    toast.success(`Page ${page} pinned · resume here next time`);
    authFetch(`${API}/page-pin`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ page, surah_number: parseInt(surahNumber) || 1 }),
    }).catch(() => {});
  };

  // Called from CompareReciters dialog: flips the user's default reciter in
  // local state AND persists to backend, so the choice survives navigation.
  const handleSetDefaultReciter = (value) => {
    setSettings((s) => ({ ...s, reciter: value }));
    authFetch(`${API}/settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        memorization_settings: { reciter: value },
      }),
    }).catch(() => {});
  };

  // Get filtered ayahs for surah mode
  const getFilteredAyahs = () => {
    if (!surah?.ayahs) return [];
    const surahNum = parseInt(surahNumber);
    if (surahNum === 9) return surah.ayahs;

    const filtered = [];
    surah.ayahs.forEach((ayah, index) => {
      if (index === 0) {
        const cleanedText = removeBismillahPrefix(ayah.text);
        if (cleanedText !== null) filtered.push({ ...ayah, text: cleanedText });
      } else {
        filtered.push(ayah);
      }
    });
    return filtered;
  };

  // Get display ayahs based on mode and range selection
  const getDisplayAyahs = () => {
    let filtered = getFilteredAyahs();

    // Apply unified range filter. Behavior depends on where the currently
    // open surah sits inside the [fromSurah..toSurah] span:
    //   • current === fromSurah === toSurah → clip to [fromAyah..toAyah]
    //   • current === fromSurah (and toSurah > fromSurah) → clip to [fromAyah..end]
    //   • current === toSurah → clip to [1..toAyah]
    //   • fromSurah < current < toSurah → whole surah (no clipping)
    //   • current outside the span → hide everything (nothing to play)
    if (rangeEnabled && filtered.length > 0) {
      const cur = parseInt(surahNumber);
      const endOfSurah = surah?.numberOfAyahs ?? filtered[filtered.length - 1].numberInSurah;
      let startAyah = 1;
      let endAyah = endOfSurah;
      if (cur < fromSurah || cur > toSurah) {
        filtered = [];
      } else {
        if (cur === fromSurah) startAyah = fromAyah;
        if (cur === toSurah) endAyah = toAyah || endOfSurah;
        filtered = filtered.filter((ayah) =>
          ayah.numberInSurah >= startAyah && ayah.numberInSurah <= endAyah
        );
      }
    }

    // Apply review mode filter
    if (reviewMode) {
      filtered = filtered.filter(ayah => {
        const key = `${surahNumber}:${ayah.numberInSurah}`;
        return progress[key];
      });
    }

    return filtered;
  };

  const displayAyahs = getDisplayAyahs();

  // In Page Mode, the playback queue is the ayahs visible on the current page(s).
  // Otherwise it's the surah-mode display list.
  const playbackQueue = viewMode === 'page' ? pageAyahs : displayAyahs;
  const queueAyahSurah = (a) => a?.surahNumber ?? parseInt(surahNumber);

  // Get total ayahs for range selector
  const totalSurahAyahs = surah?.numberOfAyahs || getFilteredAyahs().length;

  const isMemorized = (surahNum, ayahNum) => {
    const key = `${surahNum}:${ayahNum}`;
    return progress[key] || false;
  };

  // Audio engine — encapsulates playback, repeat-each-verse, repeat-set,
  // Stack Mode, page-advance, Bismillah pre-roll, and prev/next.
  const audio = useReaderAudio({
    settings,
    surah,
    surahNumber,
    viewMode,
    playbackQueue,
    stackMode,
    setStackLevel,
    setStackCurrentIndex,
    stackLevelRef,
    stackCurrentIndexRef,
    repeatEachVerseRef,
    repeatSetRef,
    pageControlsRef,
    pendingPageAdvancePlayRef,
    oneshotAudioRef,
  });
  const {
    audioRef,
    pendingPostBismillahRef,
    isPlaying,
    setIsPlaying,
    isStopped,
    currentAyah,
    currentAyahIndex,
    setCurrentAyahIndex,
    currentLoop,
    setCurrentLoop,
    currentSetLoop,
    setCurrentSetLoop,
    pageHighlight,
    setPageHighlight,
    getAudioUrl,
    playAyah,
    stopAudio,
    togglePlayPause,
    handleAudioEnded,
    goToPrevAyah,
    goToNextAyah,
  } = audio;

  // Surah-range auto-advance: detect falling edge of `isPlaying` while range
  // mode is on, and jump to the next surah with autoplay flag.
  useEffect(() => {
    const wasPlaying = prevIsPlayingRef.current;
    prevIsPlayingRef.current = isPlaying;
    if (!surahRangeEnabled) return;
    if (!wasPlaying || isPlaying) return; // we want the falling edge
    const cur = parseInt(surahNumber);
    if (Number.isNaN(cur) || cur >= toSurah) return;
    const next = cur + 1;
    navigate(`/reader/${next}?mode=${viewMode}&autoplay=1`);
  }, [isPlaying, surahRangeEnabled, surahNumber, toSurah, viewMode, navigate]);

  // If the URL includes ?autoplay=1 (we just hopped surahs in range mode),
  // start playback from ayah 1 once the new surah loads.
  useEffect(() => {
    if (searchParams.get("autoplay") !== "1") return;
    if (!surah?.ayahs?.length) return;
    if (!surahRangeEnabled) return;
    const t = setTimeout(() => {
      playAyah(parseInt(surahNumber), 1, 0);
      const next = new URLSearchParams(searchParams);
      next.delete("autoplay");
      setSearchParams(next, { replace: true });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surah, surahNumber, surahRangeEnabled]);

  // When Surah Range is toggled ON (or fromSurah/toSurah change while it's
  // enabled), if the currently-open surah is outside the range, snap to
  // `fromSurah` so the user immediately starts at the beginning of the range.
  useEffect(() => {
    if (!surahRangeEnabled) return;
    const cur = parseInt(surahNumber);
    if (Number.isNaN(cur)) return;
    if (fromSurah > toSurah) return; // invalid range — don't navigate
    if (cur < fromSurah || cur > toSurah) {
      navigate(`/reader/${fromSurah}?mode=${viewMode}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surahRangeEnabled, fromSurah, toSurah]);

  // Fetch word-by-word data on demand
  useEffect(() => {
    if (!showWordByWord || !surah) return;
    const ayah = displayAyahs[currentAyahIndex];
    if (!ayah) return;
    const surahNum = parseInt(surahNumber);
    const key = `${surahNum}:${ayah.numberInSurah}`;
    if (wbwCache[key]) return;

    setWbwLoading(true);
    fetch(`${API}/quran/wordbyword/${surahNum}/${ayah.numberInSurah}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && Array.isArray(data.words)) {
          setWbwCache(prev => ({ ...prev, [key]: data.words }));
        }
      })
      .catch(() => {})
      .finally(() => setWbwLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWordByWord, currentAyahIndex, surahNumber, surah?.ayahs?.length]);

  // Fetch tafsir on demand (flashcard mode only).
  useEffect(() => {
    if (!showTafsir || viewMode !== "flashcard" || !surah) return;
    const ayah = displayAyahs[currentAyahIndex];
    if (!ayah) return;
    const surahNum = parseInt(surahNumber);
    const key = `${tafsirEdition}|${surahNum}:${ayah.numberInSurah}`;
    if (tafsirCache[key]) return;

    setTafsirLoading(true);
    fetch(`${API}/quran/tafsir/${surahNum}/${ayah.numberInSurah}?edition=${encodeURIComponent(tafsirEdition)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && typeof data.text === "string") {
          setTafsirCache(prev => ({ ...prev, [key]: data }));
        }
      })
      .catch(() => {})
      .finally(() => setTafsirLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTafsir, tafsirEdition, viewMode, currentAyahIndex, surahNumber, surah?.ayahs?.length]);

  // Honor ?ayah=N URL param (used by "Tafsir of the Day" deep-link). Once the
  // surah is loaded, jump the flashcard index to the requested ayah and clear
  // the param so manual reload doesn't keep snapping.
  useEffect(() => {
    const ayahParam = parseInt(searchParams.get("ayah") || "");
    if (!ayahParam || !surah?.ayahs?.length) return;
    const idx = displayAyahs.findIndex((a) => a.numberInSurah === ayahParam);
    if (idx >= 0) setCurrentAyahIndex(idx);
    const next = new URLSearchParams(searchParams);
    next.delete("ayah");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surah, surahNumber]);



  const changeViewMode = (mode) => {
    setViewMode(mode);
    setSearchParams({});
    // Stop any one-shot page-mode audio when leaving page view
    if (mode !== 'page' && oneshotAudioRef.current) {
      try { oneshotAudioRef.current.pause(); } catch (e) { /* ignore */ }
    }
  };

  const toggleMemorized = async (surahNum, ayahNum) => {
    const key = `${surahNum}:${ayahNum}`;
    const newStatus = !progress[key];
    setProgress(prev => ({ ...prev, [key]: newStatus }));

    try {
      await authFetch(`${API}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          surah_number: surahNum,
          ayah_number: ayahNum,
          is_memorized: newStatus
        })
      });
      toast.success(newStatus ? 'Memorized!' : 'Unmarked');
    } catch (error) {
      console.error('Error updating progress:', error);
      setProgress(prev => ({ ...prev, [key]: !newStatus }));
    }
  };

  // Mark a list of {surahNumber, numberInSurah} ayahs as memorized, in
  // parallel. Skips ayahs already marked. Used by the Page-Mode range
  // selection toolbar.
  const markRangeMemorized = async (ayahs) => {
    if (!ayahs || ayahs.length === 0) return;
    const toMark = ayahs.filter((a) => !progress[`${a.surahNumber}:${a.numberInSurah}`]);
    if (toMark.length === 0) {
      toast.info("All selected ayahs are already memorized.");
      return;
    }
    // Optimistic local update
    setProgress((prev) => {
      const next = { ...prev };
      for (const a of toMark) next[`${a.surahNumber}:${a.numberInSurah}`] = true;
      return next;
    });
    try {
      await Promise.all(
        toMark.map((a) =>
          authFetch(`${API}/progress`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              surah_number: a.surahNumber,
              ayah_number: a.numberInSurah,
              is_memorized: true,
            }),
          })
        )
      );
      toast.success(`${toMark.length} ayah${toMark.length === 1 ? "" : "s"} marked memorized!`);
    } catch (error) {
      console.error("Error marking range memorized:", error);
      // Rollback on failure
      setProgress((prev) => {
        const next = { ...prev };
        for (const a of toMark) delete next[`${a.surahNumber}:${a.numberInSurah}`];
        return next;
      });
      toast.error("Failed to update progress. Please try again.");
    }
  };

  const addBookmark = async (surahNum, ayahNum) => {
    // Save the bookmark instantly — no note prompt. (Per user spec, the
    // note option was removed; a single quick action saves and toasts.)
    try {
      const r = await authFetch(`${API}/bookmarks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          surah_number: surahNum,
          ayah_number: ayahNum,
          note: null,
        }),
      });
      if (r.ok) {
        const data = await r.json().catch(() => null);
        if (data?.bookmark_id) {
          setBookmarkMap((m) => ({
            ...m,
            [`${surahNum}:${ayahNum}`]: data.bookmark_id,
          }));
        }
        toast.success("Bookmark saved!");
      } else {
        toast.error("Failed to add bookmark");
      }
    } catch (err) {
      console.error("addBookmark failed:", err);
      toast.error("Failed to add bookmark");
    }
  };

  const removeBookmark = async (surahNum, ayahNum) => {
    const key = `${surahNum}:${ayahNum}`;
    const id = bookmarkMap[key];
    if (!id) return;
    try {
      const r = await authFetch(`${API}/bookmarks/${id}`, { method: "DELETE" });
      if (r.ok) {
        setBookmarkMap((m) => {
          const next = { ...m };
          delete next[key];
          return next;
        });
        toast.success("Bookmark removed");
      } else {
        toast.error("Failed to remove bookmark");
      }
    } catch (err) {
      console.error("removeBookmark failed:", err);
      toast.error("Failed to remove bookmark");
    }
  };

  // Bookmark a list of ayahs at once. Auto-prefixes the note with a range
  // descriptor (e.g. "[Range 2:1–2:5]") so the bookmarks page surfaces the
  // grouping without a schema change. No user prompt — saves instantly.
  const addBookmarkRange = async (ayahs) => {
    if (!ayahs || ayahs.length === 0) return;
    const targets = ayahs.map((a) => ({
      surahNum: a.surahNumber,
      ayahNum: a.numberInSurah,
    }));
    const first = targets[0];
    const last = targets[targets.length - 1];
    const sameSurah = first.surahNum === last.surahNum;
    const notePrefix = sameSurah
      ? `[Range ${first.surahNum}:${first.ayahNum}–${last.ayahNum}]`
      : `[Range ${first.surahNum}:${first.ayahNum}–${last.surahNum}:${last.ayahNum}]`;
    try {
      const responses = await Promise.all(
        targets.map((t) =>
          authFetch(`${API}/bookmarks`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              surah_number: t.surahNum,
              ayah_number: t.ayahNum,
              note: notePrefix,
            }),
          })
        )
      );
      if (responses.every((r) => r.ok)) {
        toast.success(`${targets.length} bookmarks saved!`);
      } else {
        toast.error("Failed to save some bookmarks");
      }
    } catch (err) {
      console.error("addBookmarkRange failed:", err);
      toast.error("Failed to add bookmarks");
    }
  };

  const getTranslationText = (surahNum, ayahNum) => {
    if (!translation?.ayahs) return '';
    // Match both by surah (when present on the row) and by ayah number.
    // Page Mode can show multiple surahs on a single spread, but this
    // Reader's `translation` state is only loaded for the URL's surah —
    // so we additionally verify surahNum matches to avoid returning the
    // wrong surah's ayah when numberInSurah collides.
    const ayah = translation.ayahs.find(
      (a) =>
        a.numberInSurah === ayahNum &&
        (a.surah?.number == null || a.surah.number === surahNum),
    );
    return ayah?.text || '';
  };

  const getTransliterationText = (ayahNum) => {
    if (!transliteration?.ayahs) return '';
    const ayah = transliteration.ayahs.find(a => a.numberInSurah === ayahNum);
    return ayah?.text || '';
  };

  // Drag and drop handlers
  const handleDragStart = (e, ayah, surahNum) => {
    setDraggedAyah({ ayah, surahNum });
    e.dataTransfer.effectAllowed = 'move';
  };

  // Double-tap detector for Surah Mode ayahs handled by useAyahLongPress.
  // The hook returns { start, cancel, move, tap } — Reader.jsx wires them
  // to the existing MushafView props (startSurahAyahPress, cancelSurahAyahPress,
  // moveSurahAyahPress, handleSurahAyahTap) below.
  const surahPress = useAyahLongPress({
    holdMs: 500,
    onLongPress: (ayah, { x, y }) => {
      const surahNum = parseInt(surahNumber);
      const idx = displayAyahs.findIndex(
        (a) => a.numberInSurah === ayah.numberInSurah,
      );
      // If range is armed on a DIFFERENT ayah, a second long-press directly
      // completes the range — no menu needed.
      if (
        rangeArmedFromRef.current &&
        !(rangeArmedFromRef.current.surah === surahNum &&
          rangeArmedFromRef.current.ayah === ayah.numberInSurah)
      ) {
        completeRangeWithRef.current?.(surahNum, ayah.numberInSurah);
        return;
      }
      setAyahMenu({
        surah: surahNum,
        ayah: ayah.numberInSurah,
        idx,
        x,
        y,
      });
    },
    onTap: (ayah) => {
      const surahNum = parseInt(surahNumber);
      // If the user previously chose "Select range from here" via the menu,
      // a plain tap here completes the range. Otherwise tap is a no-op.
      completeRangeWith(surahNum, ayah.numberInSurah);
    },
  });
  const startSurahAyahPress = surahPress.start;
  const cancelSurahAyahPress = surahPress.cancel;
  const moveSurahAyahPress = surahPress.move;
  const handleSurahAyahTap = surahPress.tap;

  // Action menu state — shared by both Surah Mode and Page Mode. Long-press
  // an ayah opens this menu at the press position with Play/Bookmark/Share/
  // Translation/Select-range options.
  const [ayahMenu, setAyahMenu] = useState(null); // { surah, ayah, idx, x, y } | null
  // Ref mirror so the handleReaderTap closure (defined earlier in the
  // component body) can check the latest state without stale binding.
  const ayahMenuRef = useRef(null);
  useEffect(() => {
    ayahMenuRef.current = ayahMenu;
  }, [ayahMenu]);
  // When the user picks "Select range from here" from the menu, we arm
  // range-selection mode. The next plain tap on another ayah completes the
  // range. Auto-disarms after 10s of inactivity.
  const [rangeArmedFrom, setRangeArmedFrom] = useState(null); // { surah, ayah } | null
  const rangeArmTimerRef = useRef(null);
  // Mirror rangeArmedFrom into a ref so the long-press setTimeout closure
  // sees the latest value without re-binding handlers every render.
  const rangeArmedFromRef = useRef(null);
  useEffect(() => {
    rangeArmedFromRef.current = rangeArmedFrom;
  }, [rangeArmedFrom]);
  useEffect(() => {
    if (!rangeArmedFrom) return;
    if (rangeArmTimerRef.current) clearTimeout(rangeArmTimerRef.current);
    rangeArmTimerRef.current = setTimeout(() => setRangeArmedFrom(null), 10000);
    return () => {
      if (rangeArmTimerRef.current) clearTimeout(rangeArmTimerRef.current);
    };
  }, [rangeArmedFrom]);

  const openAyahMenuAt = (surahNum, ayahNum, idx, e) => {
    const x = e?.clientX ?? window.innerWidth / 2;
    const y = e?.clientY ?? window.innerHeight / 2;
    setAyahMenu({ surah: surahNum, ayah: ayahNum, idx, x, y });
  };

  // Translation dialog state — shows the Arabic ayah + English translation
  // in a large readable modal instead of the small sonner toast that was
  // hard to read over the parchment background.
  const [translationDialog, setTranslationDialog] = useState(null); // { surahNum, ayahNum, arabic, english } | null

  const showTranslationToast = (surahNum, ayahNum) => {
    const english = getTranslationText
      ? getTranslationText(surahNum, ayahNum)
      : "";
    // Look up the Arabic text from whichever source has it for the requested
    // (surah, ayah). In Page mode, `surah` is for the URL surah; `pageAyahs`
    // doesn't carry the Arabic text. We pass whatever we have; the dialog
    // will lazy-fetch any missing pieces.
    let arabic = "";
    if (surah?.number === surahNum) {
      const row = surah?.ayahs?.find((a) => a.numberInSurah === ayahNum);
      if (row?.text) arabic = row.text;
    }
    setTranslationDialog({ surahNum, ayahNum, arabic, english });
  };

  // Share an ayah via Web Share API (with clipboard fallback).
  const shareAyah = async (surahNum, ayahNum) => {
    const url = `${window.location.origin}/reader/${surahNum}?mode=flashcard&ayah=${ayahNum}`;
    const title = `Quran ${surahNum}:${ayahNum}`;
    const text = getTranslationText
      ? getTranslationText(surahNum, ayahNum) || ""
      : "";
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied — paste anywhere to share");
      } else {
        toast.info(url);
      }
    } catch (err) {
      // AbortError = user dismissed the native share sheet, silent by design.
      if (err?.name === "AbortError") return;
      console.warn("share failed:", err);
      toast.error("Couldn't share — copy the link manually?");
    }
  };

  // Action handlers fired from the menu. They take the menu's stored ayah.
  const handleMenuPlay = () => {
    if (!ayahMenu) return;
    setPageSelection(null);
    playAyah(ayahMenu.surah, ayahMenu.ayah, ayahMenu.idx ?? 0);
  };
  const handleMenuBookmark = () => {
    if (!ayahMenu) return;
    // Toggle: remove if already bookmarked, else add.
    if (isBookmarked(ayahMenu.surah, ayahMenu.ayah)) {
      removeBookmark(ayahMenu.surah, ayahMenu.ayah);
    } else {
      addBookmark(ayahMenu.surah, ayahMenu.ayah);
    }
  };
  const handleMenuShare = () => {
    if (!ayahMenu) return;
    shareAyah(ayahMenu.surah, ayahMenu.ayah);
  };
  const handleMenuTranslation = () => {
    if (!ayahMenu) return;
    showTranslationToast(ayahMenu.surah, ayahMenu.ayah);
  };
  const handleMenuSelectRange = () => {
    if (!ayahMenu) return;
    setRangeArmedFrom({ surah: ayahMenu.surah, ayah: ayahMenu.ayah });
    setPageHighlight({ surah: ayahMenu.surah, ayah: ayahMenu.ayah });
    setPageSelection({
      anchor: { s: ayahMenu.surah, a: ayahMenu.ayah },
      endpoint: null,
    });
    toast("Range start set", {
      description: `${ayahMenu.surah}:${ayahMenu.ayah} — tap another ayah to set the end.`,
      duration: 6000,
    });
  };

  // If a range is armed, a plain tap on any ayah completes the range.
  // Otherwise tapping an ayah does NOTHING (per user spec — no tap-to-play).
  const completeRangeWith = (surahNum, ayahNum) => {
    if (!rangeArmedFrom) return false;
    setPageSelection({
      anchor: { s: rangeArmedFrom.surah, a: rangeArmedFrom.ayah },
      endpoint: { s: surahNum, a: ayahNum },
    });
    setRangeArmedFrom(null);
    return true;
  };
  // Ref mirror so async setTimeout callbacks (long-press) can call the
  // latest completeRangeWith without stale closures.
  const completeRangeWithRef = useRef(completeRangeWith);
  useEffect(() => {
    completeRangeWithRef.current = completeRangeWith;
  });


  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropToMemorized = async (e) => {
    e.preventDefault();
    if (draggedAyah) {
      const { ayah, surahNum } = draggedAyah;
      const key = `${surahNum}:${ayah.numberInSurah}`;
      if (!progress[key]) {
        await toggleMemorized(surahNum, ayah.numberInSurah);
      }
      setDraggedAyah(null);
    }
  };

  const filteredAyahs = getFilteredAyahs();
  const memorizedCount = Object.values(progress).filter(Boolean).length;
  const totalAyahs = filteredAyahs.length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  // Handle surah selection
  const handleSurahSelect = (surahNum) => {
    setShowSurahSelector(false);
    setSurahSearch("");
    navigate(`/reader/${surahNum}`);
  };

  return (
    <div 
      data-testid="reader-page" 
      data-fullscreen={fullscreenMode ? "true" : "false"}
      className="min-h-screen bg-islamic-blue-950 reader-shell"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onPointerUp={handleReaderTap}
    >
      <audio ref={audioRef} onEnded={handleAudioEnded} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} />

      <FocusSessionChip startedAt={focusStartedAt} />

      <AyahActionMenu
        open={!!ayahMenu}
        anchor={ayahMenu ? { x: ayahMenu.x, y: ayahMenu.y } : null}
        isBookmarked={ayahMenu ? isBookmarked(ayahMenu.surah, ayahMenu.ayah) : false}
        onClose={() => setAyahMenu(null)}
        onPlay={handleMenuPlay}
        onBookmark={handleMenuBookmark}
        onShare={handleMenuShare}
        onTranslation={handleMenuTranslation}
        onSelectRange={handleMenuSelectRange}
      />

      <TranslationDialog
        open={!!translationDialog}
        onClose={() => setTranslationDialog(null)}
        surahNumber={translationDialog?.surahNum || 1}
        ayahNumber={translationDialog?.ayahNum || 1}
        initialArabic={translationDialog?.arabic || ""}
        initialTranslation={translationDialog?.english || ""}
        translationEdition={settings.translation || "en.sahih"}
      />

      {rangeArmedFrom && (
        <RangeArmedHint
          rangeArmedFrom={rangeArmedFrom}
          onCancel={() => {
            setRangeArmedFrom(null);
            setPageSelection(null);
          }}
        />
      )}

      <ReaderTutorial open={showTutorial} onClose={handleTutorialClose} />
      <ViewModePicker
        open={showViewPicker}
        onPick={(mode) => {
          changeViewMode(mode);
          setShowViewPicker(false);
        }}
        onClose={() => setShowViewPicker(false)}
      />

      {/* Surah Selector Modal */}
      <SurahSelector
        open={showSurahSelector}
        filteredSurahs={filteredSurahs}
        surahSearch={surahSearch}
        setSurahSearch={setSurahSearch}
        currentSurahNumber={parseInt(surahNumber)}
        onClose={() => { setShowSurahSelector(false); setSurahSearch(""); }}
        onSelect={handleSurahSelect}
      />

      {/* Header */}
      <ReaderHeader
        surah={surah}
        surahNumber={surahNumber}
        reciter={settings.reciter}
        onReciterChange={(v) => setSettings((s) => ({ ...s, reciter: v }))}
        reviewMode={reviewMode}
        stackMode={stackMode}
        stackLevel={stackLevel}
        viewMode={viewMode}
        currentAyahIndex={currentAyahIndex}
        displayAyahs={displayAyahs}
        totalAyahs={totalAyahs}
        selectedJuz={selectedJuz}
        changeViewMode={changeViewMode}
        onOpenSurahSelector={() => setShowSurahSelector(true)}
        onShowTutorial={() => setShowTutorial(true)}
        fullscreenMode={fullscreenMode}
        onToggleFullscreen={() => setFullscreenMode((v) => !v)}
      />

      {/* Floating exit-fullscreen FAB (only visible when chrome is hidden) */}
      {fullscreenMode && (
        <button
          data-testid="exit-fullscreen-fab"
          onClick={() => setFullscreenMode(false)}
          className="fixed top-4 right-4 z-[60] w-10 h-10 rounded-full bg-islamic-blue-900/80 backdrop-blur border border-gold-400/40 text-gold-400 hover:text-gold-300 hover:bg-islamic-blue-900 flex items-center justify-center shadow-lg"
          title="Exit distraction-free mode (Esc)"
          aria-label="Exit distraction-free mode"
        >
          <Minimize2 className="w-4 h-4" />
        </button>
      )}

      {/* Swipe hint */}
      <p className="text-center text-slate-600 text-xs font-outfit py-2">
        {pageSelection
          ? (pageSelection.endpoint
              ? 'Range selected · use the toolbar to play, bookmark, or memorize'
              : 'Long-press another ayah to end the range · or long-press the same ayah to clear')
          : (viewMode === 'page'
              ? 'Long-press for actions · swipe right for next page · left for previous'
              : viewMode === 'mushaf'
                ? 'Long-press for actions · swipe right for next surah · left for previous'
                : 'Swipe right for next surah · left for previous')}
      </p>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        {(() => {
          // In Surah Mode the selection works over displayAyahs (reshaped to
          // match the toolbar's expected {surahNumber, numberInSurah, page}
          // format so the same component handles both modes).
          const activeAyahList = viewMode === 'page'
            ? pageAyahs
            : displayAyahs.map((a) => ({
                surahNumber: parseInt(surahNumber),
                numberInSurah: a.numberInSurah,
                page: a.page,
              }));
          return (
            <PageSelectionToolbar
              selection={pageSelection}
              pageAyahs={activeAyahList}
              isAllMemorized={(() => {
                const keys = computeSelectedKeys(activeAyahList, pageSelection);
                if (!keys) return false;
                for (const k of keys) {
                  if (!progress[k]) return false;
                }
                return true;
              })()}
              onClear={() => setPageSelection(null)}
              onBookmarkRange={() => {
                const keys = computeSelectedKeys(activeAyahList, pageSelection);
                if (!keys || keys.size === 0) return;
                const selectedAyahs = activeAyahList.filter((a) =>
                  keys.has(`${a.surahNumber}:${a.numberInSurah}`)
                );
                addBookmarkRange(selectedAyahs);
              }}
              onMemorizeRange={() => {
                const keys = computeSelectedKeys(activeAyahList, pageSelection);
                if (!keys || keys.size === 0) return;
                const selectedAyahs = activeAyahList.filter((a) =>
                  keys.has(`${a.surahNumber}:${a.numberInSurah}`)
                );
                if (selectedAyahs.length === 0) return;
                if (
                  !window.confirm(
                    `Mark ${selectedAyahs.length} ayah${selectedAyahs.length === 1 ? "" : "s"} as memorized? Stack Mode will start to help you review.`
                  )
                ) {
                  return;
                }
                markRangeMemorized(selectedAyahs);
                setStackMode(true);
                setStackLevel(1);
                setStackCurrentIndex(0);
                setCurrentLoop(0);
                setCurrentSetLoop(0);
                const firstIdx = activeAyahList.findIndex((a) =>
                  keys.has(`${a.surahNumber}:${a.numberInSurah}`)
                );
                if (firstIdx >= 0) {
                  const first = activeAyahList[firstIdx];
                  playAyah(first.surahNumber, first.numberInSurah, firstIdx);
                }
              }}
              onPlayRange={() => {
                const keys = computeSelectedKeys(activeAyahList, pageSelection);
                if (!keys || keys.size === 0) return;
                const firstIdx = activeAyahList.findIndex((a) =>
                  keys.has(`${a.surahNumber}:${a.numberInSurah}`)
                );
                if (firstIdx < 0) return;
                const first = activeAyahList[firstIdx];
                setCurrentLoop(0);
                setCurrentSetLoop(0);
                if (stackMode) {
                  setStackLevel(1);
                  setStackCurrentIndex(0);
                }
                playAyah(first.surahNumber, first.numberInSurah, firstIdx);
              }}
            />
          );
        })()}
        {viewMode === 'page' ? (
          <>
            <RotateHint />
            <MushafPageView
              initialPage={(() => {
                const fromUrl = parseInt(searchParams.get("page") || "");
                if (fromUrl >= 1 && fromUrl <= 604) return fromUrl;
                return surah?.ayahs?.[0]?.page || 1;
              })()}
              pinnedPage={pinnedPage}
              onPagePin={handlePagePin}
              highlightedAyah={pageHighlight}
              selectedKeys={computeSelectedKeys(pageAyahs, pageSelection)}
              lineHeightScale={readingPreset.lineHeight / 3.6}
              fontScale={fontPreset.pageScale}
              controlsRef={pageControlsRef}
              onPageAyahsLoaded={(ayahs) => {
                // Normalize MushafPage shape ({surah_number, number_in_surah})
                // to the surah-mode queue shape ({surahNumber, numberInSurah}).
                const normalized = ayahs.map((a) => ({
                  surahNumber: a.surah_number,
                  numberInSurah: a.number_in_surah,
                  page: a.page,
                  juz: a.juz,
                }));
                setPageAyahs(normalized);
                setPageSelection(null); // selection doesn't carry across page turns
                // Continue audio after a page-turn that was triggered by the
                // end-of-spread auto-advance.
                if (pendingPageAdvancePlayRef.current && normalized.length > 0) {
                pendingPageAdvancePlayRef.current = false;
                const first = normalized[0];
                setCurrentAyahIndex(0);
                playAyah(first.surahNumber, first.numberInSurah, 0);
              }
            }}
            onAyahTap={(s, a) => {
              // Tap-to-play removed. Tap is now ONLY used to complete a
              // range when one was armed via the long-press menu's
              // "Select range from here" option.
              completeRangeWith(s, a);
            }}
            onAyahBookmark={(s, a) => addBookmark(s, a)}
            onAyahLongPress={(s, a, x, y) => {
              // If range is already armed on a different ayah, long-pressing
              // another ayah completes the range directly (no menu).
              if (
                rangeArmedFrom &&
                !(rangeArmedFrom.surah === s && rangeArmedFrom.ayah === a)
              ) {
                completeRangeWith(s, a);
                return;
              }
              const idx = pageAyahs.findIndex(
                (q) => q.surahNumber === s && q.numberInSurah === a
              );
              setAyahMenu({
                surah: s,
                ayah: a,
                idx: idx >= 0 ? idx : 0,
                x: x ?? window.innerWidth / 2,
                y: y ?? window.innerHeight / 2,
              });
            }}
            />
          </>
        ) : (
          <div className={`h-[calc(100vh-390px)] overflow-y-auto ${readingPreset.maxWidth} mx-auto`}>
            {viewMode === 'mushaf' && (
              <MushafView
                surah={surah}
                surahNumber={surahNumber}
                showTransliteration={showTransliteration}
                displayAyahs={displayAyahs}
                stackMode={stackMode}
                stackLevel={stackLevel}
                stackCurrentIndex={stackCurrentIndex}
                currentAyahIndex={currentAyahIndex}
                isPlaying={isPlaying}
                readingPreset={readingPreset}
                fontPreset={fontPreset}
                handleDragStart={handleDragStart}
                addBookmark={addBookmark}
                isMemorized={isMemorized}
                handleSurahAyahTap={handleSurahAyahTap}
                startSurahAyahPress={startSurahAyahPress}
                cancelSurahAyahPress={cancelSurahAyahPress}
                moveSurahAyahPress={moveSurahAyahPress}
                selectedKeys={computeSelectedKeys(displayAyahs.map((a) => ({ surahNumber: parseInt(surahNumber), numberInSurah: a.numberInSurah })), pageSelection)}
              />
            )}
            {viewMode === 'flashcard' && (
              <FlashcardView
                displayAyahs={displayAyahs}
                currentAyahIndex={currentAyahIndex}
                setCurrentAyahIndex={setCurrentAyahIndex}
                surahNumber={surahNumber}
                surah={surah}
                wbwCache={wbwCache}
                playAyah={playAyah}
                isPlaying={isPlaying}
                handleDragStart={handleDragStart}
                addBookmark={addBookmark}
                toggleMemorized={toggleMemorized}
                isMemorized={isMemorized}
                stackMode={stackMode}
                stackLevel={stackLevel}
                showWordByWord={showWordByWord}
                wbwLoading={wbwLoading}
                showTafsir={showTafsir}
                tafsirEdition={tafsirEdition}
                tafsirCache={tafsirCache}
                tafsirLoading={tafsirLoading}
                showTransliteration={showTransliteration}
                showTranslation={showTranslation}
                getTransliterationText={getTransliterationText}
                getTranslationText={getTranslationText}
                fontPreset={fontPreset}
                reviewMode={reviewMode}
              />
            )}
          </div>
        )}
      </main>

      {/* Audio Controls - auto-hides on idle (3s); reappears on tap/mouse move/scroll */}
      <AudioControls
        playerVisible={playerVisible}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        surahNumber={surahNumber}
        currentReciter={settings.reciter}
        onSetDefaultReciter={handleSetDefaultReciter}
        playback={{ isPlaying, isStopped, currentLoop, currentSetLoop }}
        range={{
          rangeEnabled, setRangeEnabled,
          fromAyah, setFromAyah,
          toAyah, setToAyah,
          totalSurahAyahs,
          surahRangeEnabled, setSurahRangeEnabled,
          fromSurah, setFromSurah,
          toSurah, setToSurah,
          surahList: SURAH_LIST,
        }}
        repeats={{ repeatEachVerse, setRepeatEachVerse, repeatSet, setRepeatSet }}
        modes={{
          reviewMode, setReviewMode,
          stackMode, setStackMode,
          setStackLevel, setStackCurrentIndex, setCurrentLoop,
          stackLevel, currentAyahIndex, displayAyahs,
        }}
        prefs={{
          fontSize, setFontSize,
          readingWidth, setReadingWidth,
          showTransliteration, setShowTransliteration,
          showTranslation, setShowTranslation,
          showWordByWord, setShowWordByWord,
          showTafsir, setShowTafsir,
          tafsirEdition, setTafsirEdition,
          continuousPageTurn, setContinuousPageTurn,
          continuousPageSeconds, setContinuousPageSeconds,
          viewMode,
          settings, setSettings,
        }}
        actions={{ togglePlayPause, stopAudio, goToPrevAyah, goToNextAyah }}
      />
    </div>
  );
}
